import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  model,
  output,
  untracked,
  viewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';

import { MomentModule } from 'ngx-moment';

import { AdminsAvatarsComponent } from '@core/account-ui';
import { EvaluationInsight } from '@core/evaluation-insights-types';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { AppNavigatorService } from '@core/navigation-legacy';
import { OfferStatusIndicatorComponent } from '@core/offers-ui';
import { TimeAgoPipe } from '@core/time-legacy';
import {
  BadgeComponent,
  ButtonComponent,
  ClipboardCopy,
  DividerComponent,
  HeadingComponent,
  IconButtonComponent,
  IconComponent,
  IconContainerComponent,
  PlaceloaderComponent,
  RtTooltip,
  Select2Component,
  Select2TriggerButtonComponent,
  Select2TriggerDirective,
  SparksAnimationComponent,
} from '@core/user-interface-legacy';
import { uniqByKey } from '@core/utils-legacy';

@Component({
  selector: 'rt-evaluation-insight',
  imports: [
    CommonModule,
    BadgeComponent,
    IconContainerComponent,
    OfferStatusIndicatorComponent,
    HeadingComponent,
    DividerComponent,
    IconButtonComponent,
    TranslatePipe,
    RtTooltip,
    Select2Component,
    Select2TriggerButtonComponent,
    Select2TriggerDirective,
    ReactiveFormsModule,
    AdminsAvatarsComponent,
    TimeAgoPipe,
    IconComponent,
    ButtonComponent,
    PlaceloaderComponent,
    RouterLink,
    SparksAnimationComponent,
    MomentModule,
  ],
  templateUrl: './evaluation-insight.component.html',
  styleUrl: './evaluation-insight.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.rt-card]': 'true',
  },
})
export class EvaluationInsightComponent {
  private readonly clipboardCopy = inject(ClipboardCopy);
  private readonly i18n = inject(I18nService);
  private readonly appNavigatorService = inject(AppNavigatorService);

  protected readonly insightElement = viewChild<ElementRef<HTMLElement>>('insightElement');

  public candidateId = input.required<number>();
  public insight = input.required<EvaluationInsight>();
  public isRefreshing = input(false);
  public _isLoading = input(false, { alias: 'isLoading' });
  public expandable = input(false);

  public isExpanded = model(true);

  public refresh = output<void>();
  public helpful = output<boolean>();

  protected isLoading = computed(() => this._isLoading() || this.isRefreshing());

  protected completedTabRouterLink = computed(() => {
    const candidateId = this.candidateId();
    return untracked(() =>
      this.appNavigatorService.getCandidateDialogRoute(candidateId, {
        tab: 'interviews',
        subtab: 'done',
      }),
    );
  });
  protected offer = computed(() => {
    const { offer, talentPool } = this.insight();
    return offer || (talentPool ? { ...talentPool, kind: <const>'talent_pool' } : null);
  });
  protected allUsers = computed(() =>
    uniqByKey(
      this.insight()
        .sources.map(({ evaluation }) => evaluation?.admin || evaluation?.guest)
        .filter(v => !!v),
      'id',
    ),
  );

  protected analyticsOptions = [
    { label: this.i18n.translate('app.common.boolean.yes'), value: true },
    { label: this.i18n.translate('app.common.boolean.no'), value: false },
  ];
  protected analyticsFormControl = new FormControl<boolean | null>(null);

  protected onAnalytics() {
    const { value } = this.analyticsFormControl;
    this.analyticsFormControl.setValue(null);
    this.helpful.emit(value!);
  }

  protected copyToClipboard() {
    this.clipboardCopy.fromHtmlElement(this.insightElement()!.nativeElement);
  }
}
